import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ExternalLink } from "lucide-react";
import { motion } from "framer-motion";
import { useSeo } from "../components/Seo";
import { EASE, MaskedLine, Reveal } from "../components/Reveal";
import { getCandidate, getMunicipality, ELECTION_DATE } from "../data/site";
import NotFound from "./NotFound";

const Timeline = ({ items }) => (
    <div className="relative mt-6">
        <motion.span
            className="absolute bottom-1 left-0 top-1 w-0.5 origin-top bg-gold/60"
            initial={{ scaleY: 0 }}
            whileInView={{ scaleY: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1.2, ease: EASE }}
            aria-hidden="true"
        />
        <ol className="space-y-8 pl-8">
            {items.map((t, i) => (
                <motion.li
                    key={`${t.title}-${i}`}
                    className="relative"
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.6, delay: i * 0.1, ease: EASE }}
                >
                    <motion.span
                        className="absolute -left-[39px] top-1 h-4 w-4 rounded-full border-2 border-gold bg-white"
                        initial={{ scale: 0 }}
                        whileInView={{ scale: 1 }}
                        viewport={{ once: true, margin: "-40px" }}
                        transition={{ duration: 0.4, delay: i * 0.1 + 0.15, ease: EASE }}
                        aria-hidden="true"
                    />
                    <p className="text-xs font-semibold uppercase tracking-widest text-teal">{t.period}</p>
                    <h4 className="mt-1 font-serif-display text-lg font-semibold text-navy">{t.title}</h4>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{t.text}</p>
                </motion.li>
            ))}
        </ol>
    </div>
);

export default function CandidateProfile() {
    const { slug } = useParams();
    const candidate = getCandidate(slug);
    const m = candidate ? getMunicipality(candidate.municipality) : null;

    useSeo({
        title: candidate
            ? `${candidate.name} — Candidate for ${candidate.office} | HALTONS.CA`
            : "Candidate not found — HALTONS.CA",
        description: candidate ? candidate.lede.slice(0, 155) : undefined,
        path: candidate ? `/candidates/${candidate.slug}` : "/candidates",
    });

    if (!candidate || !m) return <NotFound />;

    return (
        <main data-testid={`candidate-profile-${candidate.slug}`}>
            <section className="bg-navy text-ivory">
                <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-20">
                    <motion.nav
                        aria-label="Breadcrumb"
                        className="text-xs text-ivory/60"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.5 }}
                    >
                        <Link to="/" className="hover:text-gold">Home</Link>
                        <span className="mx-2">/</span>
                        <Link to={`/${m.slug}`} className="hover:text-gold">{m.name}</Link>
                        <span className="mx-2">/</span>
                        <span className="text-ivory/90">{candidate.name}</span>
                    </motion.nav>
                    <motion.div
                        className="mt-8 h-px w-24 origin-left bg-gold"
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
                        aria-hidden="true"
                    />
                    {candidate.series && (
                        <motion.p
                            className="mt-6 text-xs font-semibold uppercase tracking-[0.25em] text-gold"
                            data-testid="profile-series"
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.2, ease: EASE }}
                        >
                            {candidate.series}
                        </motion.p>
                    )}
                    <h1 className="mt-4 font-serif-display text-4xl font-bold leading-tight sm:text-5xl">
                        <MaskedLine delay={0.3}>{candidate.name}</MaskedLine>
                    </h1>
                    {candidate.editorialTitle && (
                        <motion.p
                            className="mt-3 font-serif-display text-xl italic text-gold sm:text-2xl"
                            data-testid="profile-editorial-title"
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.55, ease: EASE }}
                        >
                            {candidate.editorialTitle}
                        </motion.p>
                    )}
                    <motion.p
                        className="mt-4 text-xs font-semibold uppercase tracking-widest text-ivory/70"
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.65, ease: EASE }}
                    >
                        Candidate for {candidate.office} — Election day {ELECTION_DATE}
                    </motion.p>
                    <motion.p
                        className="mt-6 max-w-3xl text-base leading-relaxed text-ivory/85 md:text-lg"
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.8, ease: EASE }}
                    >
                        {candidate.lede}
                    </motion.p>
                </div>
            </section>

            {candidate.achievements.length > 0 && (
                <section className="border-b border-border bg-white" aria-labelledby="achievements-heading">
                    <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6">
                        <Reveal>
                            <h2 id="achievements-heading" className="font-serif-display text-2xl font-semibold text-navy sm:text-3xl">
                                Recognition &amp; achievements
                            </h2>
                        </Reveal>
                        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                            {candidate.achievements.map((a, i) => (
                                <Reveal key={a.title} delay={i * 0.07} className="h-full">
                                    <div className="h-full border border-border bg-ivory p-6">
                                        <div className="h-1 w-10 bg-gold" aria-hidden="true" />
                                        <h3 className="mt-4 font-serif-display text-lg font-semibold text-navy">{a.title}</h3>
                                        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{a.text}</p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            <div className="mx-auto grid max-w-6xl gap-12 px-4 py-14 sm:px-6 lg:grid-cols-[1fr_320px]">
                <div>
                    {candidate.sections.map((s) => (
                        <Reveal key={s.heading}>
                            <section className="mb-10" aria-labelledby={`section-${s.heading.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                                <h2
                                    id={`section-${s.heading.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                    className="font-serif-display text-2xl font-semibold text-navy"
                                >
                                    {s.heading}
                                </h2>
                                {s.paragraphs.map((p, i) => (
                                    <p key={i} className="mt-4 text-base leading-relaxed text-foreground/90">
                                        {p}
                                    </p>
                                ))}
                            </section>
                        </Reveal>
                    ))}

                    {candidate.timeline.length > 0 && (
                        <section className="mb-10" aria-labelledby="timeline-heading">
                            <Reveal>
                                <h2 id="timeline-heading" className="font-serif-display text-2xl font-semibold text-navy">
                                    Career &amp; public-service timeline
                                </h2>
                            </Reveal>
                            <Timeline items={candidate.timeline} />
                        </section>
                    )}

                    {candidate.priorities && candidate.priorities.items.length > 0 && (
                        <section className="mb-10" aria-labelledby="priorities-heading">
                            <Reveal>
                                <h2 id="priorities-heading" className="font-serif-display text-2xl font-semibold text-navy">
                                    Stated campaign priorities
                                </h2>
                                <p className="mt-3 border-l-2 border-teal pl-4 text-sm italic text-muted-foreground">
                                    {candidate.priorities.note}
                                </p>
                            </Reveal>
                            <div className="mt-6 space-y-4">
                                {candidate.priorities.items.map((p, i) => (
                                    <Reveal key={p.title} delay={i * 0.07}>
                                        <div className="border border-border bg-white p-5">
                                            <h3 className="font-serif-display text-lg font-semibold text-navy">{p.title}</h3>
                                            {p.text && (
                                                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{p.text}</p>
                                            )}
                                        </div>
                                    </Reveal>
                                ))}
                            </div>
                        </section>
                    )}

                    {candidate.editorial && (
                        <Reveal>
                            <aside
                                data-testid="profile-editorial-note"
                                className="mb-10 border border-teal/40 bg-teal/5 p-6"
                                aria-labelledby="editorial-note-heading"
                            >
                                <h2 id="editorial-note-heading" className="text-xs font-semibold uppercase tracking-widest text-teal">
                                    {candidate.editorial.heading}
                                </h2>
                                <p className="mt-3 text-sm leading-relaxed text-foreground/90">
                                    {candidate.editorial.text}
                                </p>
                            </aside>
                        </Reveal>
                    )}

                    {candidate.media.length > 0 && (
                        <section className="mb-10" aria-labelledby="media-heading">
                            <Reveal>
                                <h2 id="media-heading" className="font-serif-display text-2xl font-semibold text-navy">
                                    Media &amp; public statements
                                </h2>
                            </Reveal>
                            <ul className="mt-4 space-y-2">
                                {candidate.media.map((item) => (
                                    <li key={item.url}>
                                        <a href={item.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-semibold text-teal link-underline">
                                            {item.title}
                                            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    {candidate.sources.length > 0 && (
                        <section className="mb-10" aria-labelledby="sources-heading">
                            <Reveal>
                                <h2 id="sources-heading" className="font-serif-display text-2xl font-semibold text-navy">
                                    Sources
                                </h2>
                            </Reveal>
                            <ul className="mt-4 space-y-2">
                                {candidate.sources.map((s) => (
                                    <li key={s.url}>
                                        <a href={s.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-semibold text-teal link-underline">
                                            {s.title}
                                            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}
                </div>

                <aside>
                    <Reveal className="sticky top-24">
                        <div className="border border-border bg-white p-6" data-testid="profile-facts">
                            <p className="text-xs font-semibold uppercase tracking-widest text-teal">Profile facts</p>
                            <dl className="mt-4 space-y-4 text-sm">
                                <div>
                                    <dt className="text-xs uppercase tracking-widest text-muted-foreground">Office sought</dt>
                                    <dd className="mt-1 font-semibold text-navy">{candidate.office}</dd>
                                </div>
                                <div>
                                    <dt className="text-xs uppercase tracking-widest text-muted-foreground">Municipality</dt>
                                    <dd className="mt-1 font-semibold text-navy">{m.name}</dd>
                                </div>
                                <div>
                                    <dt className="text-xs uppercase tracking-widest text-muted-foreground">Election day</dt>
                                    <dd className="mt-1 font-semibold text-navy">{ELECTION_DATE}</dd>
                                </div>
                                <div>
                                    <dt className="text-xs uppercase tracking-widest text-muted-foreground">Profile last updated</dt>
                                    <dd className="mt-1 font-semibold text-navy">{candidate.lastUpdated}</dd>
                                </div>
                            </dl>
                            <div className="mt-6 border-t border-border pt-5">
                                <Link
                                    to="/corrections"
                                    data-testid="profile-correction-link"
                                    className="text-sm font-semibold text-teal link-underline"
                                >
                                    Report a correction
                                </Link>
                                <Link
                                    to={`/${m.slug}`}
                                    data-testid="profile-back-link"
                                    className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-navy link-underline"
                                >
                                    <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                                    All {m.name} candidates
                                </Link>
                            </div>
                        </div>
                    </Reveal>
                </aside>
            </div>

            {candidate.featured && (
                <section className="border-t border-border bg-navy text-ivory">
                    <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-4 px-4 py-10 sm:px-6 md:flex-row md:items-center">
                        <Reveal>
                            <p className="max-w-2xl text-sm text-ivory/75">
                                This profile is part of <em className="font-serif-display">Emerging Leaders of Halton</em>,
                                a HALTONS.CA editorial series. It is a selected editorial feature — not an
                                official ranking or endorsement.
                            </p>
                            <Link to="/issues" data-testid="profile-series-link" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-gold link-underline">
                                More from Issues &amp; Stories
                                <ArrowRight className="h-4 w-4" aria-hidden="true" />
                            </Link>
                        </Reveal>
                    </div>
                </section>
            )}
        </main>
    );
}
